import axios from 'axios';
import assign from 'lodash-es/assign';

export default class StorageService
{
    public static uploadUrl = (): string => `${axios.defaults.baseURL}/admin/storage/upload`;

    public static async upload(file: File, container: string, payload: any = null, secured: boolean = false, forceConvert: boolean = false, forceResize: boolean = false)
    {
        const data = new FormData();

        data.append('container', container);
        data.append('payload', JSON.stringify(payload));
        data.append('secured', secured ? 'true' : 'false');
        data.append('file', file);
        data.append('forceConvert', forceConvert ? 'true' : 'false');
        data.append('forceResize', forceResize ? 'true' : 'false');

        const result = await axios.post<FileResource>('admin/storage/upload', data);

        return result.data;
    }

    public static async uploadMany(files: File[], container: string, payload: string = null, secured: boolean = false)
    {
        const result: FileResource[] = [];

        for (let i = 0; i < files.length; i++)
        {
            result.push(await StorageService.upload(files[i], container, payload, secured));
        }

        return result;
    }

    public static previewResource(publicId: string)
    {
        return `${axios.defaults.baseURL}/admin/storage/resources/preview/${publicId}`;
    }

    public static async fetchResource(publicId: string)
    {
        return (await axios.get<FileResource>(`admin/storage/resources/${publicId}`)).data;
    }

    public static async getResources(scope: string, folder: number, search: string, filterFromUtc: string, filterDueUtc: string, all: boolean, pager: Pager, publicIds: string[] = undefined)
    {
        const data = pager.data();

        assign(data, {
            scope,
            folder,
            filter: search,
            filterFromUtc,
            filterDueUtc,
            all,
            publicIds
        });

        const result = await axios.get<Resources>(`admin/storage/resources`, {
            params: data
        });

        return result.data;
    }

    public static getFolders(items: FileResource[])
    {
        return items
            .filter(function (item)
            {
                return 'hasChildren' in item || item.resourceType === 'folder';
            })
            .map(function (item)
            {
                return item.id;
            });
    }

    public static getFiles(items: FileResource[])
    {
        return items
            .filter(function (item)
            {
                return item.resourceType === 'file';
            })
            .map(function (item)
            {
                return item.id;
            });
    }

    public static async deleteResources(items: FileResource[])
    {
        const folders = StorageService.getFolders(items);
        const files = StorageService.getFiles(items);

        const result = await axios.delete<Statement>(`admin/storage/resources`, {
            params: {
                folders: folders.join(','),
                files: files.join(',')
            }
        });

        return result.data;
    }

    public static async moveResources(scope: string, destination: number, items: FileResource[])
    {
        const folders = StorageService.getFolders(items);
        const files = StorageService.getFiles(items);

        const result = await axios.put<MovedResources>(`admin/storage/resources/move`, {
            scope: scope,
            destination: destination,
            folders: folders,
            files: files
        });

        return result.data;
    }

    public static async createFolder(model: FolderModel)
    {
        return (await axios.post<FolderModel>(`admin/storage/folders`, model)).data;
    }

    public static async renameFolder(id: number, name: string)
    {
        return (await axios.put<Statement>(`admin/storage/folders/${id}/rename`, { folderName: name })).data;
    }

    public static async renameFile(id: number, name: string)
    {
        return (await axios.put<Statement>(`admin/storage/files/${id}/rename`, { fileName: name })).data;
    }

    public static async saveContent(id: number, model: Record<string, Content>)
    {
        return (await axios.put<Statement>(`admin/storage/files/${id}/content`, { content: model })).data;
    }
    public static async updateMeta(id: number, model: Record<string, string>)
    {
        return (await axios.put<Statement>(`admin/storage/files/${id}/meta`, model)).data;
    }

    public static async cropImage(id: number, model: CropModel)
    {
        return (await axios.post<FileResource>(`admin/storage/files/${id}/crop`, model)).data;
    }

    public static async resizeImage(id: number, model: ResizeModel)
    {
        return (await axios.post<FileResource>(`admin/storage/files/${id}/resize`, model)).data;
    }

    public static async getFolderNodes(scope: string, expanded: number[]): Promise<Pagination<Resource<FolderNodeModel>>>
    {
        return (await axios.get<Pagination<Resource<FolderNodeModel>>>('admin/storage/folders', {
            params: {
                scope: scope,
                expanded: expanded.join(',')
            }
        })).data;
    }
}

export type Scope = 'public' | 'private' | 'selected';

export interface FolderPermissions
{
    canView: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canManage: boolean;
}

export interface Resources extends Pagination<FileResource>
{
    breadcrumbs: Record<number, string>;
}

export interface Content
{
    name: string;
    lead: string;
    alt: string;
    tags: string;
    author: string;
    licence: string;
    source: string;
}

export interface MovedResources
{
    destination: number;
    folders: number[];
    files: number[];
}

export interface FolderModel
{
    scope: string;
    parentId?: number;
    folderName: string;
}

export interface CropModel
{
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface ResizeModel
{
    width: number;
    height: number;
}

export interface FolderNodeModel
{
    id: number;
    parentId?: number;
    name: string;
    hasChildren: boolean;
    isActive?: boolean;
    root?: boolean;
}
